import React, { useState } from 'react';
import { Crown, Check, Zap, Star, Sparkles, Gift, Users, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePageMeta } from '@/hooks/usePageMeta';

export const Membership = () => {
  usePageMeta('Wolves Pass Membership — Coming Soon', 'SoundWolves Wolves Pass memberships are launching soon. Preview the Wolves Pass, Gold and Black tiers and pricing.');
  const [billingCycle, setBillingCycle] = useState('monthly');

  const tiers = [
    {
      name: 'Wolves Pass',
      tagline: 'For Casual Night Owls',
      icon: Zap,
      price: { monthly: 29, yearly: 290 },
      color: 'text-secondary',
      gradient: 'gradient-wolf',
      features: [
        'Early ticket access (24h before public)',
        'Exclusive member-only events (1/month)',
        '10% discount on all ticket purchases',
        'Priority customer support',
        'Access to Hype Squad community',
        'Referral rewards program',
        'Monthly event newsletter'
      ],
      cta: 'Join Wolves Pass'
    },
    {
      name: 'Gold',
      tagline: 'For True Party Enthusiasts',
      icon: Star,
      price: { monthly: 79, yearly: 790 },
      color: 'text-primary',
      gradient: 'gradient-primary',
      popular: true,
      features: [
        'Everything in Wolves Pass',
        'Early ticket access (48h before public)',
        'Exclusive member-only events (3/month)',
        '20% discount on all purchases',
        'VIP table upgrades available',
        '2 complimentary guest passes/month',
        'Backstage access to select events',
        'Personalized event recommendations',
        'City Hype Squad leader opportunities',
        'Exclusive merchandise & swag'
      ],
      cta: 'Upgrade to Gold'
    },
    {
      name: 'Black',
      tagline: 'The Ultimate VIP Experience',
      icon: Crown,
      price: { monthly: 199, yearly: 1990 },
      color: 'text-accent',
      gradient: 'gradient-vip',
      features: [
        'Everything in Gold',
        'Unlimited early access to all events',
        'Invite-only experiences & after-parties',
        '30% discount on all purchases',
        'Complimentary VIP table access (1/month)',
        '5 complimentary guest passes/month',
        'Meet & greet with DJs and artists',
        'Private event booking concierge',
        'Dedicated account manager',
        'Priority venue entry (skip the line)',
        'Access to Black member lounge',
        'Exclusive brand collaborations',
        'Annual VIP retreat invitation'
      ],
      cta: 'Join the Elite'
    }
  ];

  const perks = [
    {
      icon: Calendar,
      title: 'Priority Booking',
      description: 'Get first dibs on limited-capacity events before they sell out'
    },
    {
      icon: Users,
      title: 'Hype Squad',
      description: 'Join city-based crews, unlock team rewards, and shape local nightlife'
    },
    {
      icon: Gift,
      title: 'Exclusive Rewards',
      description: 'Earn points, unlock perks, and get access to members-only experiences'
    },
    {
      icon: Sparkles,
      title: 'VIP Treatment',
      description: 'Skip lines, access premium areas, and enjoy elevated experiences'
    }
  ];

  return (
    <div className="min-h-screen pt-20 pb-24 md:pb-8">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1643981693404-d76e58594bbf?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="VIP Lounge"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/80 to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <Badge className="mb-4 bg-primary/20 text-primary border-primary/30 backdrop-blur-sm">
            <Sparkles className="w-3 h-3 mr-1" />
            Coming Soon
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6 leading-tight">
            Join the <span className="text-gradient-gold">Wolves Pack</span>
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Unlock exclusive VIP experiences, priority access, and connect with the most vibrant nightlife community in North America
          </p>
          <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-5 py-2 text-sm font-semibold text-primary">
            <Sparkles className="w-4 h-4" />
            Memberships are launching soon — here’s a preview of the tiers &amp; pricing
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4">
        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <Tabs value={billingCycle} onValueChange={setBillingCycle} className="w-full max-w-md">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="yearly">
                Yearly
                <Badge className="ml-2 bg-success/20 text-success border-success/30">
                  Save 17%
                </Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Membership Tiers */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-20">
          {tiers.map((tier) => {
            const Icon = tier.icon;
            const price = billingCycle === 'monthly' ? tier.price.monthly : tier.price.yearly;
            const savings = billingCycle === 'yearly' ? Math.round(tier.price.monthly * 12 - tier.price.yearly) : 0;

            return (
              <Card
                key={tier.name}
                className={`relative overflow-hidden border-border/50 hover-lift ${
                  tier.popular ? 'lg:scale-105 border-primary shadow-glow' : ''
                }`}
              >
                {tier.popular && (
                  <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-4 py-1 text-xs font-bold">
                    MOST POPULAR
                  </div>
                )}

                <CardHeader className="text-center pb-4">
                  <div className={`w-16 h-16 ${tier.gradient} rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl font-display mb-1">{tier.name}</CardTitle>
                  <CardDescription>{tier.tagline}</CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="text-center">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold">${price}</span>
                      <span className="text-muted-foreground">/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
                    </div>
                    {savings > 0 && (
                      <p className="text-sm text-success mt-1">
                        Save ${savings}/year
                      </p>
                    )}
                    <Badge className="mt-3 bg-amber-500/15 text-amber-500 border-amber-500/30">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Coming Soon
                    </Badge>
                  </div>

                  <Button
                    variant={tier.popular ? 'premium' : 'default'}
                    className="w-full"
                    size="lg"
                    disabled
                    aria-disabled="true"
                    title="Memberships are launching soon"
                  >
                    Coming Soon
                  </Button>

                  <div className="space-y-3 pt-4">
                    {tier.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 ${tier.color} flex-shrink-0 mt-0.5`} />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Perks Section */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-3">
              Member <span className="text-gradient-wolf">Perks</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              What makes SOUNDWOLVES membership special
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {perks.map((perk) => {
              const Icon = perk.icon;
              return (
                <Card key={perk.title} className="text-center border-border/50">
                  <CardContent className="p-6 space-y-3">
                    <div className="w-12 h-12 gradient-wolf rounded-xl mx-auto flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-display font-bold">{perk.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {perk.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-3">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'Can I cancel my membership anytime?',
                a: 'Yes! You can cancel your membership at any time. Your benefits will continue until the end of your billing period.'
              },
              {
                q: 'Do unused guest passes roll over?',
                a: 'Guest passes are valid for the month they\'re issued. Unused passes do not roll over to the next month.'
              },
              {
                q: 'Can I upgrade or downgrade my tier?',
                a: 'Absolutely! You can change your membership tier at any time. Upgrades take effect immediately, while downgrades apply at the next billing cycle.'
              },
              {
                q: 'Are there any additional fees?',
                a: 'No hidden fees! The membership price includes all listed benefits. Event tickets are purchased separately with your member discount.'
              }
            ].map((faq, index) => (
              <Card key={index} className="border-border/50">
                <CardContent className="p-6">
                  <h3 className="font-display font-bold mb-2">{faq.q}</h3>
                  <p className="text-muted-foreground">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Membership;